/**
 * Escape-hatch tipado para Supabase quando tabela/RPC ainda nao esta em
 * `Database` types regenerado (schema drift, RPCs missing migration, etc).
 *
 * Tecnica: helper recebe `client: unknown` + narrow estrutural single-step
 * — o caller passa `SupabaseClient<Database>` (upcast implicito para
 * `unknown`); dentro do helper, narrow para `UntypedSupabaseClient`. Sem
 * `as unknown as`. Apos `supabase gen types` regenerar tipos, migrar
 * consumers para o cliente tipado.
 *
 * Cliente passado por parametro (e nao import global) para evitar
 * acoplamento runtime com `@supabase/supabase-js` no pacote types-only.
 */

/** Builder estrutural minimo. `unknown` no retorno forca o caller a fazer narrow. */
export type UntypedQueryBuilder = {
  select: (cols?: string) => UntypedQueryBuilder;
  insert: (values: Record<string, unknown>) => UntypedQueryBuilder;
  update: (values: Record<string, unknown>) => UntypedQueryBuilder;
  delete: () => UntypedQueryBuilder;
  upsert: (values: Record<string, unknown>) => UntypedQueryBuilder;
  eq: (col: string, value: unknown) => UntypedQueryBuilder;
  neq: (col: string, value: unknown) => UntypedQueryBuilder;
  in: (col: string, values: unknown[]) => UntypedQueryBuilder;
  /**
   * `or('a.is.null,a.eq.x')` — grupo OR do PostgREST, combinado com AND aos
   * demais filtros. Monte a expressao num modulo proprio e testado (ex.:
   * `lesson-sport-scope.ts`): a sintaxe e separada por virgula, entao um id
   * malformado nao da erro, da um filtro DIFERENTE do pretendido.
   */
  or: (filters: string) => UntypedQueryBuilder;
  gte: (col: string, value: unknown) => UntypedQueryBuilder;
  lte: (col: string, value: unknown) => UntypedQueryBuilder;
  order: (col: string, opts?: { ascending?: boolean }) => UntypedQueryBuilder;
  limit: (n: number) => UntypedQueryBuilder;
  single: () => Promise<{ data: unknown; error: { message: string } | null }>;
  maybeSingle: () => Promise<{
    data: unknown;
    error: { message: string } | null;
  }>;
  then: <R>(
    onfulfilled: (value: {
      data: unknown;
      error: { message: string } | null;
    }) => R,
  ) => Promise<R>;
};

/** Shape minimo do cliente Supabase usado por este modulo. */
type UntypedSupabaseClient = {
  from: (table: string) => UntypedQueryBuilder;
  rpc: (
    fn: string,
    args?: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>;
};

/**
 * Acesso untyped a uma tabela. Caller faz narrow do resultado para seu shape.
 *
 * @example
 *   const result = await fromTable(supabase, "drift_table").select("*").eq("id", id).maybeSingle();
 *   const row = result.data as DriftRow | null;
 */
export function fromTable(
  client: unknown,
  table: string,
): UntypedQueryBuilder {
  return (client as UntypedSupabaseClient).from(table);
}

/**
 * Chamada untyped a RPC. Caller declara o tipo de retorno T.
 *
 * @example
 *   const { data, error } = await callRpc<MyResult>(supabase, "my_rpc", { p_id: id });
 */
export async function callRpc<T = unknown>(
  client: unknown,
  fnName: string,
  args: Record<string, unknown> = {},
): Promise<{ data: T | null; error: Error | null }> {
  const result = await (client as UntypedSupabaseClient).rpc(fnName, args);
  return result as { data: T | null; error: Error | null };
}
